import { getLocale, useIntl } from 'umi';
import { FeedsStateType } from './useRequestFeedsState';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LocaleType } from '@op/types/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

export type RequestFuncMapType = {
  defaultRequest: (
    params: any,
    cancelFunc: any,
  ) => Promise<{ report_en: string; report_cn: string }>;
  dealwithParams?: (params: any) => any;
  dealwithData?: (
    data: any,
    feedsState: FeedsStateType,
  ) => { report_en: string; report_cn: string };
};

export default function useDefaultSummary({
  filter,
  requestFuncMap = undefined,
  requestFeedsStateData,
  stateLoading = false,
  linkFilterChange = true,
  getGptLoading,
}: {
  filter: any;
  requestFuncMap?: RequestFuncMapType;
  requestFeedsStateData: any;
  stateLoading?: boolean;
  linkFilterChange?: boolean;
  getGptLoading?: (loading: boolean) => void;
}) {
  const { formatMessage } = useIntl();

  const [defaultGPT, setDefaultGPT] = useState<{
    report_en?: string;
    report_cn?: string;
  }>({});

  function clearDefaultGpt() {
    setDefaultGPT({});
  }

  const [defaultLoading, setDefaultLoading] = useState(
    requestFuncMap !== undefined,
  );

  const replaceText = (text: string, replacements: any) => {
    let replacedText = text || '';
    Object.keys(replacements).forEach((key) => {
      const placeholder = `$${key}`;
      replacedText = replacedText.replaceAll(
        placeholder,
        String(replacements[key]),
      );
    });
    return replacedText;
  };

  const { wrapperRequest } = useCancelRequestHook();

  async function requestData() {
    if (requestFuncMap === undefined) return;
    setDefaultLoading(true);
    // 如果有feedsState就不进行请求了
    const replacements = await requestFeedsStateData();

    // console.log(replacements, "------------")

    const {
      dealwithData = (data: any) => {
        const keys: Array<keyof typeof data> = ['report_en', 'report_cn'];
        keys.forEach((key) => {
          data[key] = replaceText(data[key], replacements);
        });
        return data;
      },
      defaultRequest,
      dealwithParams,
    } = requestFuncMap;
    const params = dealwithParams ? dealwithParams(filter) : filter;
    wrapperRequest((cancelFunc) =>
      defaultRequest(params, cancelFunc)
        .then((res: any) => {
          const data = dealwithData(res, replacements);
          setDefaultGPT(data);
        })
        .finally(() => {
          setDefaultLoading(false);
        }),
    );
  }

  const { loading, defaultGptText } = useMemo(() => {
    const language: LocaleType = getLocale();
    const defaultGptText =
      language === 'en-US' ? defaultGPT.report_en : defaultGPT.report_cn;
    getGptLoading?.(defaultLoading || stateLoading);
    return {
      loading: defaultLoading || stateLoading,
      defaultGptText,
    };
  }, [defaultGPT, formatMessage, defaultLoading, stateLoading]);

  const isFirst = useRef(true);
  useEffect(() => {
    if (filter?.edition_unified_id && (linkFilterChange || isFirst.current)) {
      requestData();
      isFirst.current = false;
    }
  }, [filter, requestFuncMap === undefined]);

  return {
    loading,
    defaultGptText,
    clearDefaultGpt,
    defaultGPT,
  };
}
